import PropTypes from 'prop-types';
import React from 'react';
import { i18n } from '../i18n/translate';
import { getActiveRuleSetFromUrl } from '../utils/UrlUtil';
import { SelectFilter } from './Filters/SelectFilter';
import { RuleSetModal } from './RuleSetModal';

const ROLE_OPTIONS = Object.freeze([
  {
    ruleSet: 'he_admin',
    name: 'he_admin',
    label: i18n.t('Higher Education Students'),
    shortLabel: i18n.t('Students'),
  },
  {
    ruleSet: 'fac_staff_admin',
    name: 'fac_staff_admin',
    label: i18n.t('Faculty & Staff'),
    shortLabel: i18n.t('Faculty & Staff'),
  },
  {
    ruleSet: 'cc_admin',
    name: 'cc_admin',
    label: i18n.t('Employees'),
    shortLabel: i18n.t('Employees'),
  },
  {
    ruleSet: 'scorm_manager',
    name: 'scorm_manager',
    label: i18n.t('Scorm'),
    shortLabel: i18n.t('Scorm'),
  },
]);

const getSkipRoleSelectionValue = () => {
  // Don't check on server render
  if (typeof document !== 'undefined') {
    return !!document?.querySelector('[data-skip-role-selection="true"]');
  }

  return false;
};

export const SidebarRoleSwitcher = ({ currentRuleSets = [], onMenuClose, onMenuOpen, onSelectRuleSet }) => {
  const activeRuleSet = getActiveRuleSetFromUrl(currentRuleSets);
  const availableRuleSets = ROLE_OPTIONS.filter(roleOption =>
    currentRuleSets.some(currentRuleSet => currentRuleSet.name === roleOption.ruleSet)
  );
  const defaultRuleSet = availableRuleSets.find(roleOption => roleOption.ruleSet === activeRuleSet?.name);

  const onChange = selectedRuleSet => {
    onSelectRuleSet(selectedRuleSet.name);
  };

  /*
    No Role selection if any of the following:
    - Explicit skip from component outside Sidebar scope (a.k.a rendered by RoR). Bug#2830782
    - There is an active rule set but it is not within the TRAIN rulesets (cc_admin, fac_staff_admin, he_admin, scorm_manager).
    - There is not more that one train role to choose from.
  */
  if (getSkipRoleSelectionValue() || (activeRuleSet?.name && !defaultRuleSet) || availableRuleSets.length < 2) {
    return null;
  } else {
    if (defaultRuleSet) {
      return (
        <div
          className="sidebar-role-switcher"
          data-object-type="sidebar-role-switcher"
          data-action="sidebar-role-switcher"
        >
          <SelectFilter
            defaultValue={defaultRuleSet}
            isClearable={false}
            name="rule_sets"
            onChange={onChange}
            onMenuClose={onMenuClose}
            onMenuOpen={onMenuOpen}
            options={availableRuleSets}
            selectedKey="shortLabel"
            showSelected={true}
            valueKey="ruleSet"
          />
        </div>
      );
    } else {
      // if no active rule set and more than two available rule sets
      const firstMatchingRuleSet = currentRuleSets.find(ruleSet =>
        availableRuleSets.some(availableRuleSet => availableRuleSet.ruleSet === ruleSet.name)
      );

      let homeHref = firstMatchingRuleSet ? firstMatchingRuleSet.interstitial_redirect : '';
      homeHref = homeHref ? homeHref.slice(0, homeHref.indexOf('?')) : '';

      const ruleSets = availableRuleSets.map(currentRuleSet => currentRuleSet.ruleSet);
      return <RuleSetModal href={homeHref} ruleSets={ruleSets} show={true} />;
    }
  }
};

SidebarRoleSwitcher.propTypes = {
  currentRuleSets: PropTypes.arrayOf(
    PropTypes.shape({
      active: PropTypes.bool,
      interstitial_redirect: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  onMenuClose: PropTypes.func,
  onMenuOpen: PropTypes.func,
  onSelectRuleSet: PropTypes.func,
};

SidebarRoleSwitcher.displayName = 'SidebarRoleSwitcher';
